import React, { FC, useCallback, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import * as yup from 'yup'

import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import BookingContext from '../contexts/booking'
import Button from '@mui/material/Button'

import {
  IRecord_CRMContact_Import,
} from '@kaidam/fc-shared/src/types/schema/crm'

import {
  IData_Booking_General_Config,
} from '@kaidam/fc-shared/src/types/schema/booking'

import Form from '@kaidam/fc-frontend/src/components/form/Wrapper'
import {
  crmSchemaAll,
  crmSchemaAllPublic,
  prebookingSchemaAll,
  crmValidation,
  allValues,
} from '@kaidam/fc-frontend/src/components/booking/forms'

import {
  getDaysFromToday,
} from '@kaidam/fc-shared/src/booking/utils'

import {
  handlers,
  getErrorMessage,
} from '@kaidam/fc-shared/src/frontend/store/utils/api'

import NoCoverageMessage from '../components/widgets/NoCoverageMessage'
import OffSeasonMessage from '../components/widgets/OffSeasonMessage'

import networkActions from '@kaidam/fc-shared/src/frontend/store/modules/network'
import snackbarActions from '@kaidam/fc-shared/src/frontend/store/modules/snackbar'
import routerActions from '@kaidam/fc-shared/src/frontend/store/modules/router'
import routerSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/router'

const BookingDetails: FC<React.PropsWithChildren<unknown>> = () => {
  const dispatch = useDispatch()
  const [ hasSubmitted, setHasSubmitted ] = useState(false)

  const initialValues = useMemo(() => {
    const crm = JSON.parse(JSON.stringify(allValues.crm))
    if(!crm.postcode) {
      const urlParams = new URLSearchParams(window.location.search)
      crm.postcode = urlParams.get('postcode')
    }
    return {
      crm,
    }
  }, [
    allValues,
  ])

  const isOffSeason = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('is_off_season') == 'yes'
  }, [])

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      crm: crmValidation,
    })
  }, [
    crmValidation,
  ])

  const submitForm = useCallback(async (data: any) => {
    dispatch(networkActions.setGlobalLoading({
      active: true,
    }))
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const orgid = urlParams.get('org')
      await handlers.post(`/ft_booking_public/register_interest/${orgid || 'root'}`, data.crm)
      dispatch(snackbarActions.setSuccess(`Your interest has been registered and we will be in touch soon!`))
      setHasSubmitted(true)
    } catch(e) {
      dispatch(snackbarActions.setError(getErrorMessage(e)))
    }
    dispatch(networkActions.setGlobalLoading({
      active: false,
    }))
  }, [])

  if(hasSubmitted) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', padding: 2 }}>
        <Paper sx={{ width: '100%', padding: 3 }}>
          <div>
            <Typography
              align="left"
              gutterBottom
              variant="h6"
              sx={{ marginTop: 3 }}
            >
              Thank you!
            </Typography>
            <Typography
              align="left"
              gutterBottom
              variant="body1"
            >
              Thanks for getting in touch, we will reply as soon as we can offer a booking for your area!
            </Typography>
          </div>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ display: 'flex', padding: 2 }}>
      <Paper sx={{ width: '100%', padding: 3 }}>
        <Form
          validationSchema={ validationSchema }         
          error={ "" }
          initialValues={ initialValues }
          handlers={{
            disabled: (props) => {
              if(props.name == 'crm.postcode') return true
              return false
            },
          }}
          onSubmit={ submitForm }
        >
          {
            ({
              render,
              setFieldValue,
              values,
              showErrors,
              errors,
              isValid,
              onSubmit,
            }) => {
              return (
                <div>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="h6"
                    sx={{ marginTop: 3 }}
                  >
                    Register your interest...
                  </Typography>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="body1"
                    sx={{ marginTop: 2, marginBottom: 2 }}
                  >
                    { isOffSeason ? <OffSeasonMessage /> : <NoCoverageMessage /> }
                  </Typography>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="h6"
                    sx={{ marginTop: 3 }}
                  >
                    Your details
                  </Typography>
                  {
                    render(crmSchemaAll)
                  }
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ marginTop: 2 }}
                    onClick={ onSubmit }
                  >
                    Submit
                  </Button>
                </div>
              )
            }
          }               
        </Form>
      </Paper>
    </Container>
  )
}

export default BookingDetails